import { scrollToTop } from 'shared/utils/pageUtils';
import { parseCommentDetailsWithIndicatorDetails } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import { CommentDetails } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails/ImpactDetailStageIcons';
import { parseCommentDetailsWithIndicatorWithUpdate } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactSummary';
import {
  FeedbackCommentBody,
  getCustomQuestionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  FeedbackSectionId,
  FeedbackSection,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { CustomQuestionSectionId } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { IndicatorWithUpdate } from '../ReportBuilder/Components/ImpactIndicator/utils';
import { IndicatorDetails } from '../ReportBuilder/Components/IndicatorDetails/utils';
import { AnswersFeedbacks } from './api';

export const scrollToTopOnFeedback = () => {
  if (document.documentElement.scrollTop > 500) {
    scrollToTop(310);
  }
};
export const defaultFeedbackSectionName = 'Overview';

// this may come from API in the future
export const allFeedbackSections: FeedbackSection[] = [
  // { label: 'Allocations', sectionId: 'allocation' },
  { label: 'Description', sectionId: 'description' },
  { label: 'Dates', sectionId: 'dates' },
  { label: 'Location', sectionId: 'location' },
  { label: 'Activities', sectionId: 'activities' },
  { label: 'Impact Model Builder', sectionId: 'impact_summary' },
  { label: 'Impact Model Details', sectionId: 'indicator_details' },
  { label: 'Beneficiaries', sectionId: 'beneficiaries' },
  { label: 'Narrative', sectionId: 'narrative' },
  { label: 'Budget', sectionId: 'budget' },
];
export const allFeedbackSectionsWithCustomQuestions: FeedbackSection[] = [
  ...allFeedbackSections,
  { label: 'Custom Questions', sectionId: 'custom_questions' },
];
export const allFeedbackSectionsWithAllocations: FeedbackSection[] = [
  { label: 'Allocations', sectionId: 'allocation' },
  ...allFeedbackSections,
];
export const allFeedbackSectionsWithCustomQuestionsAndAllocations: FeedbackSection[] = [
  ...allFeedbackSections,
  { label: 'Custom Questions', sectionId: 'custom_questions' },
  { label: 'Allocations', sectionId: 'allocation' },
];

export const feedbackSectionIdToString = (
  sectionId: CustomQuestionSectionId,
): string => {
  switch (sectionId) {
    case 'description':
      return 'Description';
    case 'dates':
      return 'Dates';
    case 'location':
      return 'Location';
    case 'impact_summary':
      return 'Impact Model Builder';
    case 'activities':
      return 'Activities';
    case 'indicator_details':
      return 'Impact Model Details';
    case 'beneficiaries':
      return 'Beneficiaries';
    case 'budget':
      return 'Budget';
    case 'narrative':
      return 'Narrative';

    default:
      return 'Miscellaneous';
  }
};

export const labelToSectionId = (
  label: string,
): FeedbackSectionId | undefined => {
  switch (label) {
    case 'Description':
      return 'description';
    case 'Dates':
      return 'dates';
    case 'Location':
      return 'location';
    case 'Activities':
      return 'activities';
    case 'Impact Model Builder':
      return 'impact_summary';
    case 'Impact Model Details':
      return 'indicator_details';
    case 'Beneficiaries':
      return 'beneficiaries';
    case 'Budget':
      return 'budget';
    case 'Narrative':
      return 'narrative';
    case 'Program Overview':
    case 'Final Results':
    case 'Interim Forecasts':
    case 'Review & Submit':
      return 'finalize_report';

    case 'Progress Notes':
      return 'interim_report';

    default:
      return undefined;
  }
};

export interface SelectedFeedback {
  question:
    | {
        questionId: number;
        questionText: string;
      }
    | undefined;
  section: FeedbackSection;
}

export interface FeedbackCommentBodyWithIndex extends FeedbackCommentBody {
  index: number;
}

export interface NPOFeedbackSection {
  overAllSection: FeedbackCommentBodyWithIndex[];
  feedbackByQuestionId: {
    [questionId: number]: FeedbackCommentBodyWithIndex[];
  };
}

export type FeedbackBySections = {
  [sectionId in FeedbackSectionId]+?: NPOFeedbackSection;
};

export const getSectionById = (
  sectionId: FeedbackSectionId,
  allFeedbackSections: FeedbackSection[],
): FeedbackSection | undefined =>
  allFeedbackSections.find(x => x.sectionId === sectionId);

export const IMPACT_SUMMARY_QUESTION_ID = 40015;
export const INDICATOR_DETAILS_QUESTION_ID = 50001;
export const INDICATOR_SUMMARY_QUESTION_ID = 50002;
export const parseCommentDetails = (
  questionId?: number,
  text?: string,
):
  | CommentDetails<IndicatorWithUpdate>
  | CommentDetails<IndicatorDetails>
  | undefined => {
  if (!text) return undefined;
  switch (questionId) {
    case IMPACT_SUMMARY_QUESTION_ID:
      return parseCommentDetailsWithIndicatorWithUpdate(text);
    case INDICATOR_DETAILS_QUESTION_ID:
      return parseCommentDetailsWithIndicatorDetails(text);
    default:
      return undefined;
  }
};

export const getFeedbackList = (
  sectionNames: FeedbackSection[],
  feedbackBySections: FeedbackBySections,
  selectedSection: SelectedFeedback,
) => {
  const feedbackByQuestions: SelectedFeedback[] = [];
  const feedbackSections: {
    [sectionId in FeedbackSectionId]: SelectedFeedback[];
  } = {
    allocation: [],
    custom_questions: [],
    beneficiaries: [],
    budget: [],
    description: [],
    dates: [],
    finalize_report: [],
    indicator_details: [],
    impact_summary: [],
    activities: [],
    location: [],
    narrative: [],
    interim_report: [],
  };
  let setIndex: number | undefined = undefined;
  const questionId = selectedSection.question?.questionId;
  const result: SelectedFeedback[] = [];
  for (let i = 0; i < sectionNames.length; i++) {
    const sectionId = sectionNames[i].sectionId;
    const questions = feedbackBySections[sectionId]
      ? Object.keys(feedbackBySections[sectionId]!.feedbackByQuestionId)
      : [];
    const overAllSectionArray = feedbackBySections[sectionId]?.overAllSection;
    if (overAllSectionArray && overAllSectionArray.length > 0) {
      result.push({ question: undefined, section: sectionNames[i] });
      if (!questionId && sectionId === selectedSection.section.sectionId) {
        setIndex = result.length - 1;
      }
    }
    for (let j = 0; j < questions.length; j++) {
      const questionId = parseInt(questions[j]);
      const question = feedbackBySections[sectionId]!.feedbackByQuestionId[
        questionId
      ][0];
      const questionSection: SelectedFeedback = {
        section: sectionNames[i],
        question: {
          questionId,
          questionText: question?.questionText || `Unknown - ${questionId}`,
        },
      };

      result.push(questionSection);
      feedbackByQuestions.push(questionSection);
      feedbackSections[sectionId] = feedbackSections[sectionId] || [];
      feedbackSections[sectionId].push(questionSection);
      if (questions[j] === selectedSection.question?.questionId.toString()) {
        setIndex = result.length - 1;
      }
    }
  }

  return { result, setIndex, feedbackSections, feedbackByQuestions };
};

export const getPreviousAndNextFeedback = (
  selectedSection: SelectedFeedback,
  feedbackBySections: FeedbackBySections,
  sectionNames: FeedbackSection[],
): {
  previousFeedback: SelectedFeedback | undefined;
  nextFeedback: SelectedFeedback | undefined;
} => {
  const { result, setIndex } = getFeedbackList(
    sectionNames,
    feedbackBySections,
    selectedSection,
  );

  if (setIndex === undefined) {
    return {
      nextFeedback: undefined,
      previousFeedback: undefined,
    };
  }

  const previousFeedback: SelectedFeedback | undefined =
    setIndex === 0 ? undefined : result[setIndex - 1];
  const nextFeedback: SelectedFeedback | undefined =
    setIndex === result.length - 1 ? undefined : result[setIndex + 1];

  return {
    nextFeedback,
    previousFeedback,
  };
};

export const mapToSections = (
  comments: FeedbackCommentBody[],
  allFeedbackSections: FeedbackSection[],
) => {
  const result: FeedbackBySections = {};
  const sections: FeedbackSection[] = [];
  for (let i = 0; i < comments.length; i++) {
    const c: FeedbackCommentBodyWithIndex = { ...comments[i], index: i };
    const questionId = c.surveyQuestionId
      ? Number(getCustomQuestionId(c.surveyQuestionId))
      : c.questionId;
    const sectionId: FeedbackSectionId = c.surveyQuestionId
      ? 'custom_questions'
      : c.sectionId;

    const qid = typeof questionId === 'number' ? questionId : undefined;

    const isNewSection = !result[sectionId];
    if (result[sectionId]) {
      if (qid !== undefined) {
        if (result[sectionId]!.feedbackByQuestionId[qid]) {
          result[sectionId]!.feedbackByQuestionId[qid].push(c);
        } else {
          result[sectionId]!.feedbackByQuestionId[qid] = [c];
        }
      } else {
        result[sectionId]!.overAllSection.push(c);
      }
    } else {
      if (qid !== undefined) {
        result[sectionId] = {
          feedbackByQuestionId: { [qid]: [c] },
          overAllSection: [],
        };
      } else {
        result[sectionId] = {
          feedbackByQuestionId: {},
          overAllSection: [c],
        };
      }
    }
    if (isNewSection) {
      const section = getSectionById(sectionId, allFeedbackSections);
      section && sections.push(section);
    }
  }
  return {
    sections,
    feedbackBySections: result,
  };
};

export interface VerificationProgress {
  resolved?: string;
  lastResolved?: string;
  updatedAt?: string;
  resolvedCount?: number;
  tiVerified?: string;
  tiUnverified?: string;
  donorVerified?: string;
  donorUnverified?: string;
}

export interface AllVerificationsProgress {
  [x: string]: VerificationProgress;
}

export const readyForSubmission = (
  total: number,
  userType: UserType,
  feedbackStatus: FeedbackStatus,
  progress: AllVerificationsProgress,
) => {
  const result: string[] = [];
  const keys = Object.keys(progress);

  for (const k of keys) {
    const verification = progress[k];
    switch (feedbackStatus) {
      case 'Awaiting Funder Verification':
        if (verification.donorVerified || verification.donorUnverified) {
          result.push(k);
        }
        break;

      case 'Awaiting TI Verification':
        if (verification.tiVerified || verification.tiUnverified) {
          result.push(k);
        }
        break;

      case 'TI Feedback Provided':
      case 'Funder Feedback Provided':
        if (verification.resolved) {
          result.push(k);
        }
        break;

      default:
        break;
    }
  }
  return { completed: result, ready: result.length === total };
};

export const getProgress = (
  feedback: AnswersFeedbacks['allAnswersFeedback'],
): AllVerificationsProgress => {
  const result: AllVerificationsProgress = {};
  for (const f of feedback) {
    const surveyQuestionId = f.surveyQuestionId
      ? getCustomQuestionId(f.surveyQuestionId)
      : undefined;
    result[f.questionId || surveyQuestionId || f.sectionId] = {
      resolved: f.resolved || undefined,
      lastResolved: f.lastResolved || undefined,
      updatedAt: f.updatedAt || undefined,
      resolvedCount: f.resolvedCount,
      tiVerified: f.verified || undefined,
      donorVerified: f.verified || undefined,
    };
  }
  return result;
};
